<template>
	<div class="date-range">
		<div v-if="showTitle" class="title">{{ $t("filters.data-range") }}</div>

		<div class="date-presets">
			<button
				v-for="preset in PRESET_DEFS"
				:key="preset.key"
				@click="selectPreset(preset.key)"
				class="preset-btn"
				:class="{ active: mode === preset.key }"
			>
				{{ preset.label }}
			</button>
			<button
				ref="customTriggerRef"
				@click="selectCustomMode"
				class="preset-btn"
				:class="{ active: mode === 'custom' }"
			>
				{{ $t("filters.date-picker.date-custom") }}
			</button>
		</div>

		<Teleport to="body" :disabled="inlineCustomCalendar">
			<div
				v-if="isCustomOpen"
				ref="flyoutRef"
				class="date-custom-panel"
				:class="{ inline: inlineCustomCalendar }"
				:style="inlineCustomCalendar ? {} : { top: flyoutStyle.top, left: flyoutStyle.left }"
			>
				<div class="calendars">
					<DatePicker.Root
						:key="`start-${calendarResetKey}`"
						inline
						selectionMode="single"
						@valueChange="onStartDateChange"
						:startOfWeek="1"
						:locale="datePickerLocale"
						:min="minSelectableDate"
						:max="maxSelectableDate"
						:closeOnSelect="false"
						minView="day"
						maxView="day"
					>
						<div class="date-content">
							<DatePicker.View view="day">
								<DatePicker.Context v-slot="api">
									<div class="date-view-control">
										<DatePicker.MonthSelect class="date-select" />
										<DatePicker.YearSelect class="date-select" />
									</div>

									<DatePicker.Table class="date-table">
										<DatePicker.TableHead>
											<DatePicker.TableRow>
												<DatePicker.TableHeader v-for="(weekDay, id) in api.weekDays" :key="id" class="date-header">
													{{ weekDay.narrow }}
												</DatePicker.TableHeader>
											</DatePicker.TableRow>
										</DatePicker.TableHead>
										<DatePicker.TableBody>
											<DatePicker.TableRow v-for="(week, id) in api.weeks" :key="id">
												<DatePicker.TableCell v-for="(day, id) in week" :key="id" :value="day">
													<DatePicker.TableCellTrigger class="date-day">
														{{ day.day }}
													</DatePicker.TableCellTrigger>
												</DatePicker.TableCell>
											</DatePicker.TableRow>
										</DatePicker.TableBody>
									</DatePicker.Table>
								</DatePicker.Context>
							</DatePicker.View>
						</div>
					</DatePicker.Root>

					<DatePicker.Root
						:key="`end-${calendarResetKey}`"
						inline
						selectionMode="single"
						@valueChange="onEndDateChange"
						:startOfWeek="1"
						:locale="datePickerLocale"
						:min="minSelectableDate"
						:max="maxSelectableDate"
						:closeOnSelect="false"
						minView="day"
						maxView="day"
					>
						<div class="date-content">
							<DatePicker.View view="day">
								<DatePicker.Context v-slot="api">
									<div class="date-view-control">
										<DatePicker.MonthSelect class="date-select" />
										<DatePicker.YearSelect class="date-select" />
									</div>

									<DatePicker.Table class="date-table">
										<DatePicker.TableHead>
											<DatePicker.TableRow>
												<DatePicker.TableHeader v-for="(weekDay, id) in api.weekDays" :key="id" class="date-header">
													{{ weekDay.narrow }}
												</DatePicker.TableHeader>
											</DatePicker.TableRow>
										</DatePicker.TableHead>
										<DatePicker.TableBody>
											<DatePicker.TableRow v-for="(week, id) in api.weeks" :key="id">
												<DatePicker.TableCell v-for="(day, id) in week" :key="id" :value="day">
													<DatePicker.TableCellTrigger class="date-day">
														{{ day.day }}
													</DatePicker.TableCellTrigger>
												</DatePicker.TableCell>
											</DatePicker.TableRow>
										</DatePicker.TableBody>
									</DatePicker.Table>
								</DatePicker.Context>
							</DatePicker.View>
						</div>
					</DatePicker.Root>
				</div>

				<div class="custom-footer">
					<input
						v-if="editingField === 'start'"
						ref="startFooterInput"
						type="text"
						:value="startDateDisplay"
						@input="onStartInput"
						@blur="onStartBlur"
						:placeholder="$t('filters.date-picker.placeholder')"
						class="footer-input"
					/>
					<button v-else class="edit-link" @click="startEditing">
						{{ summaryStart ? formatSummaryDate(summaryStart) : $t("filters.date-picker.date-start-placeholder") }}
					</button>

					<span>→</span>

					<input
						v-if="editingField === 'end'"
						ref="endFooterInput"
						type="text"
						:value="endDateDisplay"
						@input="onEndInput"
						@blur="onEndBlur"
						:placeholder="$t('filters.date-picker.placeholder')"
						class="footer-input"
					/>
					<button v-else class="edit-link" @click="endEditing">
						{{ summaryEnd ? formatSummaryDate(summaryEnd) : $t("filters.date-picker.date-end-placeholder") }}
					</button>

					<button class="reset-btn" @click="resetCustomSelection">{{ $t("filters.date-picker.date-reset") }}</button>
				</div>
			</div>
		</Teleport>

		<div v-if="summaryStart && summaryEnd && !isCustomOpen" class="date-summary">
			<span>{{ formatSummaryDate(summaryStart) }} → {{ formatSummaryDate(summaryEnd) }}</span>
			<button class="reset-btn" @click="resetDateRange">{{ $t("filters.date-picker.date-reset") }}</button>
		</div>
	</div>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick, onMounted, onUnmounted } from "vue"
import { storeToRefs } from "pinia"
import { DatePicker, parseDate, type DatePickerValueChangeDetails } from "@ark-ui/vue"
import { onClickOutside } from "@vueuse/core"
import { useI18n } from "vue-i18n"
import { UseRootStore } from "@/model/RootStore"

interface DateRange {
	start: Date | null
	end: Date | null
}

type PresetKey =
	| "last30Days"
	| "last3Months"
	| "last6Months"
	| "last12Months"
	| "last2Years"
	| "last5Years"
	| "currentYear"

const props = withDefaults(
	defineProps<{
		startDate?: Date | null
		endDate?: Date | null
		showTitle?: boolean
		// renders the custom calendars in normal flow, above the date summary, instead of as a
		// floating panel - suits a compact popover (e.g. the table quick filters) better than the
		// side flyout used by the wider vertical filter panels
		inlineCustomCalendar?: boolean
	}>(),
	{
		showTitle: true,
		inlineCustomCalendar: false,
	}
)

const emit = defineEmits<{
	dateRangeChange: [DateRange]
}>()

const { t } = useI18n()

const store = UseRootStore()
const { currentLocale } = storeToRefs(store)

const datePickerLocale = computed(() => {
	return currentLocale.value === "fr" ? "fr-FR" : "en-US"
})

const today = new Date()
const minSelectableDate = parseDate(new Date(today.getFullYear() - 100, 0, 1))
const maxSelectableDate = parseDate(today)

const PRESET_DEFS = computed(() => [
	{ key: "last30Days" as const, label: t("filters.date-picker.date-past-30-days") },
	{ key: "last3Months" as const, label: t("filters.date-picker.date-past-3-months") },
	{ key: "last6Months" as const, label: t("filters.date-picker.date-past-6-months") },
	{ key: "last12Months" as const, label: t("filters.date-picker.date-past-12-months") },
	{ key: "last2Years" as const, label: t("filters.date-picker.date-past-2-years") },
	{ key: "last5Years" as const, label: t("filters.date-picker.date-past-5-years") },
	{ key: "currentYear" as const, label: t("filters.date-picker.date-current-year") },
])

function computePresetRange(key: PresetKey): DateRange {
	const now = new Date()

	if (key === "currentYear") {
		return { start: new Date(now.getFullYear(), 0, 1), end: now }
	}

	const start = new Date(now)
	switch (key) {
		case "last30Days":
			start.setDate(start.getDate() - 30)
			break
		case "last3Months":
			start.setMonth(start.getMonth() - 3)
			break
		case "last6Months":
			start.setMonth(start.getMonth() - 6)
			break
		case "last12Months":
			start.setMonth(start.getMonth() - 12)
			break
		case "last2Years":
			start.setFullYear(start.getFullYear() - 2)
			break
		case "last5Years":
			start.setFullYear(start.getFullYear() - 5)
			break
	}

	return { start, end: now }
}

function isSameDay(date1: Date, date2: Date): boolean {
	return date1.toDateString() === date2.toDateString()
}

function matchingPreset(start: Date, end: Date): PresetKey | null {
	for (const { key } of PRESET_DEFS.value) {
		const range = computePresetRange(key)
		if (range.start && range.end && isSameDay(range.start, start) && isSameDay(range.end, end)) {
			return key
		}
	}
	return null
}

const mode = ref<PresetKey | "custom" | null>(
	props.startDate && props.endDate ? matchingPreset(props.startDate, props.endDate) : null
)

const startDateValue = ref<string[]>(props.startDate ? [formatDateToISO(props.startDate)] : [])
const endDateValue = ref<string[]>(props.endDate ? [formatDateToISO(props.endDate)] : [])
const startDateDisplay = ref(props.startDate ? formatDateToISO(props.startDate) : "")
const endDateDisplay = ref(props.endDate ? formatDateToISO(props.endDate) : "")
const editingField = ref<"start" | "end" | null>(null)
const startFooterInput = ref<HTMLInputElement | null>(null)
const endFooterInput = ref<HTMLInputElement | null>(null)

// calendars are uncontrolled, so bumping this key remounts them to clear the selected day on reset
const calendarResetKey = ref(0)

// the custom-range calendars open as a flyout so they aren't squeezed by the filter panel's width
const isCustomOpen = ref(false)
const customTriggerRef = ref<HTMLButtonElement | null>(null)
const flyoutRef = ref<HTMLElement | null>(null)
const flyoutStyle = ref({ top: "0px", left: "0px" })

const FLYOUT_WIDTH = 460
const FLYOUT_MARGIN = 8

function updateFlyoutPosition() {
	if (props.inlineCustomCalendar) return

	const trigger = customTriggerRef.value
	if (!trigger) return

	const rect = trigger.getBoundingClientRect()
	const flyoutWidth = Math.min(FLYOUT_WIDTH, window.innerWidth - FLYOUT_MARGIN * 2)

	const fitsOnRight = rect.right + FLYOUT_MARGIN + flyoutWidth <= window.innerWidth
	const left = fitsOnRight
		? rect.right + FLYOUT_MARGIN
		: Math.max(FLYOUT_MARGIN, rect.left - FLYOUT_MARGIN - flyoutWidth)

	const flyoutHeight = flyoutRef.value?.offsetHeight ?? 420
	const top = Math.max(FLYOUT_MARGIN, Math.min(rect.top, window.innerHeight - flyoutHeight - FLYOUT_MARGIN))

	flyoutStyle.value = { top: `${top}px`, left: `${left}px` }
}

function handleReposition() {
	if (isCustomOpen.value) updateFlyoutPosition()
}

onMounted(() => {
	window.addEventListener("resize", handleReposition)
	window.addEventListener("scroll", handleReposition, true)
})

onUnmounted(() => {
	window.removeEventListener("resize", handleReposition)
	window.removeEventListener("scroll", handleReposition, true)
})

onClickOutside(customTriggerRef, () => (isCustomOpen.value = false), { ignore: [flyoutRef] })

const summaryStart = computed(() => (startDateValue.value[0] ? parseISODate(startDateValue.value[0]) : null))
const summaryEnd = computed(() => (endDateValue.value[0] ? parseISODate(endDateValue.value[0]) : null))

function formatSummaryDate(date: Date): string {
	return new Intl.DateTimeFormat(datePickerLocale.value, { day: "numeric", month: "short", year: "numeric" }).format(
		date
	)
}

async function startEditing() {
	editingField.value = "start"
	await nextTick()
	startFooterInput.value?.focus()
}

async function endEditing() {
	editingField.value = "end"
	await nextTick()
	endFooterInput.value?.focus()
}

function formatDateInput(value: string): string {
	const numbers = value.replace(/\D/g, "")
	if (!numbers) return ""
	if (numbers.length <= 4) {
		return numbers
	} else if (numbers.length <= 6) {
		return `${numbers.slice(0, 4)}-${numbers.slice(4)}`
	} else {
		return `${numbers.slice(0, 4)}-${numbers.slice(4, 6)}-${numbers.slice(6, 8)}`
	}
}

type DatePrecision = "year" | "month" | "day"

interface ParsedFlexibleDate {
	year: number
	month?: number
	day?: number
	precision: DatePrecision
}

// Accepts partial input (just a year, or a year and month) alongside a full date,
// so a boundary date can be inferred without forcing the user to pick a full day.
function parseFlexibleDate(value: string): ParsedFlexibleDate | null {
	const yearOnly = /^(\d{4})$/.exec(value)
	if (yearOnly) {
		return { year: Number(yearOnly[1]), precision: "year" }
	}

	const yearMonth = /^(\d{4})-(\d{2})$/.exec(value)
	if (yearMonth) {
		const month = Number(yearMonth[2])
		if (month < 1 || month > 12) return null
		return { year: Number(yearMonth[1]), month, precision: "month" }
	}

	const yearMonthDay = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
	if (yearMonthDay) {
		const year = Number(yearMonthDay[1])
		const month = Number(yearMonthDay[2])
		const day = Number(yearMonthDay[3])
		const date = new Date(year, month - 1, day)
		if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) return null
		return { year, month, day, precision: "day" }
	}

	return null
}

// A partial date is filled to a concrete boundary: the start of the period for the
// lower bound, the end of the period for the upper bound (e.g. "2024" as a start
// resolves to Jan 1st, but to Dec 31st as an end).
function toBoundaryDate(parsed: ParsedFlexibleDate, boundary: "start" | "end"): Date {
	if (parsed.precision === "day") {
		return new Date(parsed.year, (parsed.month as number) - 1, parsed.day as number)
	}
	if (parsed.precision === "month") {
		const month = parsed.month as number
		return boundary === "start" ? new Date(parsed.year, month - 1, 1) : new Date(parsed.year, month, 0)
	}
	return boundary === "start" ? new Date(parsed.year, 0, 1) : new Date(parsed.year, 11, 31)
}

function formatDateToISO(date: Date): string {
	return date.toISOString().split("T")[0]
}

function parseISODate(dateStr: string): Date {
	return new Date(dateStr + "T00:00:00")
}

function emitDateChange() {
	const start = startDateValue.value[0] ? parseISODate(startDateValue.value[0]) : null
	const end = endDateValue.value[0] ? parseISODate(endDateValue.value[0]) : null
	emit("dateRangeChange", { start, end })
}

// Applies the value as soon as it's parseable, instead of waiting for blur -
// otherwise the filter only takes effect once the user clicks away, which reads as unresponsive.
function onStartInput(event: Event) {
	const target = event.target as HTMLInputElement
	const formatted = formatDateInput(target.value)
	target.value = formatted

	if (!formatted) {
		startDateValue.value = []
		startDateDisplay.value = ""
		emitDateChange()
		return
	}

	const parsed = parseFlexibleDate(formatted)
	if (parsed) {
		startDateValue.value = [formatDateToISO(toBoundaryDate(parsed, "start"))]
		startDateDisplay.value = formatted
		emitDateChange()
	}
}

function onEndInput(event: Event) {
	const target = event.target as HTMLInputElement
	const formatted = formatDateInput(target.value)
	target.value = formatted

	if (!formatted) {
		endDateValue.value = []
		endDateDisplay.value = ""
		emitDateChange()
		return
	}

	const parsed = parseFlexibleDate(formatted)
	if (parsed) {
		endDateValue.value = [formatDateToISO(toBoundaryDate(parsed, "end"))]
		endDateDisplay.value = formatted
		emitDateChange()
	}
}

// Only needed to discard a trailing invalid fragment (e.g. "2024-1"); valid and
// empty values are already committed live by the input handlers above.
function onStartBlur(event: Event) {
	const target = event.target as HTMLInputElement
	const value = target.value.trim()

	if (value && !parseFlexibleDate(value)) {
		target.value = startDateDisplay.value
	}
	editingField.value = null
}

function onEndBlur(event: Event) {
	const target = event.target as HTMLInputElement
	const value = target.value.trim()

	if (value && !parseFlexibleDate(value)) {
		target.value = endDateDisplay.value
	}
	editingField.value = null
}

// use toString(), not valueAsString, which is locale-formatted (e.g. "04/08/2026") instead of ISO
function onStartDateChange(details: DatePickerValueChangeDetails) {
	if (details.value?.[0]) {
		const iso = details.value[0].toString()
		startDateValue.value = [iso]
		startDateDisplay.value = iso
		emitDateChange()
	}
}

function onEndDateChange(details: DatePickerValueChangeDetails) {
	if (details.value?.[0]) {
		const iso = details.value[0].toString()
		endDateValue.value = [iso]
		endDateDisplay.value = iso
		emitDateChange()
	}
}

function applyRange(start: Date | null, end: Date | null) {
	startDateValue.value = start ? [formatDateToISO(start)] : []
	endDateValue.value = end ? [formatDateToISO(end)] : []
	startDateDisplay.value = start ? formatDateToISO(start) : ""
	endDateDisplay.value = end ? formatDateToISO(end) : ""
	emit("dateRangeChange", { start, end })
}

function selectPreset(key: PresetKey) {
	mode.value = key
	editingField.value = null
	isCustomOpen.value = false
	const range = computePresetRange(key)
	applyRange(range.start, range.end)
}

// clicking again while already in custom mode just toggles the flyout back open
function selectCustomMode() {
	isCustomOpen.value = mode.value === "custom" ? !isCustomOpen.value : true
	mode.value = "custom"
	if (isCustomOpen.value) nextTick(updateFlyoutPosition)
}

function resetDateRange() {
	mode.value = null
	editingField.value = null
	isCustomOpen.value = false
	applyRange(null, null)
}

// unlike resetDateRange, stays in custom mode and only clears the selection
function resetCustomSelection() {
	editingField.value = null
	calendarResetKey.value++
	applyRange(null, null)
}

// re-derives the active preset pill when the parent pushes a range from outside (e.g. a filters-wide reset)
function syncModeFromProps() {
	if (!props.startDate && !props.endDate) {
		// don't let resetCustomSelection's null/null echo close the custom panel
		if (mode.value !== "custom") mode.value = null
		return
	}
	if (props.startDate && props.endDate) {
		const preset = matchingPreset(props.startDate, props.endDate)
		if (preset) mode.value = preset
	}
}

watch(
	() => props.startDate,
	newDate => {
		if (newDate) {
			const iso = formatDateToISO(newDate)
			// If this is just the parent echoing back the boundary date we ourselves emitted,
			// keep the precision-aware display (e.g. "2024") instead of expanding it to the
			// full date, which would silently hide that only a year/month was chosen.
			if (iso === startDateValue.value[0]) return
			startDateValue.value = [iso]
			startDateDisplay.value = iso
		} else {
			startDateValue.value = []
			startDateDisplay.value = ""
		}
		syncModeFromProps()
	}
)

watch(
	() => props.endDate,
	newDate => {
		if (newDate) {
			const iso = formatDateToISO(newDate)
			if (iso === endDateValue.value[0]) return
			endDateValue.value = [iso]
			endDateDisplay.value = iso
		} else {
			endDateValue.value = []
			endDateDisplay.value = ""
		}
		syncModeFromProps()
	}
)
</script>

<style scoped>
.date-range {
	width: 100%;

	.title {
		font-family: var(--font-family-primary);
		font-size: var(--font-size-small);
		font-weight: var(--font-weight-regular);
		color: var(--neutral-600);
		margin-bottom: var(--spacing-element-gap);
	}

	.date-presets {
		display: flex;
		gap: var(--spacing-element-gap);
		flex-wrap: wrap;
		margin-bottom: var(--spacing-element-gap);

		.preset-btn {
			padding: var(--spacing-xs) calc(var(--spacing-sm) + var(--spacing-xxs));
			font-size: var(--font-size-small);
			background: var(--neutral-0);
			border: var(--border-width-thin) solid var(--neutral-200);
			border-radius: var(--border-radius-full);
			cursor: pointer;
			color: var(--neutral-600);
			transition: all 0.2s;

			&:hover {
				background: var(--neutral-200);
			}

			&.active {
				background: var(--primary-green);
				color: var(--neutral-0);
				border-color: var(--primary-green);
			}
		}
	}

	.date-summary {
		display: flex;
		align-items: center;
		justify-content: space-between;
		padding-top: var(--spacing-sm);
		border-top: var(--border-width-thin) solid var(--neutral-200);
		font-size: var(--font-size-small);
		color: var(--neutral-600);

		.reset-btn {
			background: none;
			border: none;
			padding: 0;
			cursor: pointer;
			color: var(--blue-dark);
			font-size: var(--font-size-small);
			text-decoration: underline;

			&:hover {
				color: var(--neutral-900);
			}
		}
	}

	@media (max-width: 480px) {
		.date-presets {
			justify-content: center;
		}
	}
}

.date-custom-panel {
	position: fixed;
	z-index: 1000;
	width: min(460px, calc(100vw - var(--spacing-md) * 2));
	max-height: calc(100vh - var(--spacing-md) * 2);
	overflow-y: auto;
	background: var(--neutral-0);
	border: var(--border-width-thin) solid var(--neutral-200);
	border-radius: var(--border-radius-md);
	box-shadow: var(--shadow-lg);
	padding: var(--spacing-sm);

	&.inline {
		position: static;
		z-index: auto;
		max-height: none;
		border: none;
		box-shadow: none;
		margin-bottom: var(--spacing-sm);
	}

	.calendars {
		display: flex;
		gap: var(--spacing-sm);

		.date-content {
			flex: 1;
			min-width: 0;
			background: var(--neutral-0);
			border: var(--border-width-thin) solid var(--neutral-200);
			border-radius: var(--border-radius-base);
			padding: var(--spacing-sm) var(--spacing-xs);
		}

		.date-view-control {
			display: flex;
			justify-content: center;
			gap: var(--spacing-xxs);
			margin-bottom: var(--spacing-xs);

			.date-select {
				padding: var(--spacing-xxs) var(--spacing-xs);
				border: none;
				border-radius: var(--border-radius-sm);
				font-size: var(--font-size-xs);
				font-weight: var(--font-weight-semibold);
				background: var(--neutral-0);
				color: var(--blue-dark);
				cursor: pointer;
				text-align: center;
				text-align-last: center;
				text-transform: capitalize;

				&:hover {
					background: var(--neutral-200);
				}
			}
		}

		.date-table {
			width: 100%;
			border-collapse: separate;
			border-spacing: var(--spacing-xxs);
			table-layout: fixed;
			text-align: center;

			.date-header {
				padding: var(--spacing-xs) 0;
				text-align: center;
				font-size: var(--font-size-xs);
				color: var(--neutral-600);
				font-weight: var(--font-weight-medium);
			}

			.date-day {
				width: 100%;
				aspect-ratio: 1;
				border: none;
				background: none;
				border-radius: var(--border-radius-sm);
				cursor: pointer;
				font-size: var(--font-size-xs);
				display: inline-flex;
				align-items: center;
				justify-content: center;
				color: var(--neutral-600);

				&:hover {
					background: var(--neutral-200);
				}

				&[data-outside-range] {
					color: var(--neutral-200);
					cursor: default;

					&:hover {
						background: none;
					}
				}

				&[data-selected] {
					background: var(--primary-green);
					color: var(--neutral-0);
				}

				&[data-today] {
					position: relative;

					&::after {
						content: "";
						position: absolute;
						bottom: 1px;
						left: 50%;
						transform: translateX(-50%);
						width: 3px;
						height: 3px;
						background: var(--primary-green);
						border-radius: 50%;
					}
				}
			}
		}
	}

	.custom-footer {
		display: flex;
		align-items: center;
		flex-wrap: wrap;
		gap: var(--spacing-xs);
		margin-top: var(--spacing-element-gap);
		padding-top: var(--spacing-sm);
		border-top: var(--border-width-thin) solid var(--neutral-200);
		font-size: var(--font-size-small);
		color: var(--neutral-600);

		.edit-link {
			background: none;
			border: none;
			padding: 0;
			cursor: pointer;
			color: var(--blue-dark);

			&:hover {
				color: var(--neutral-900);
			}
		}

		.reset-btn {
			margin-left: auto;
			background: none;
			border: none;
			padding: 0;
			cursor: pointer;
			color: var(--blue-dark);
			font-size: var(--font-size-small);
			text-decoration: underline;

			&:hover {
				color: var(--neutral-900);
			}
		}

		.footer-input {
			min-width: var(--width-medium-input);
			padding: var(--spacing-xxs) var(--spacing-xs);
			border: var(--border-width-thin) solid var(--neutral-200);
			border-radius: var(--border-radius-sm);
			font-size: var(--font-size-small);

			&:focus {
				outline: none;
				border-color: var(--primary-green);
			}
		}
	}
}
</style>
